import type { ValidationType } from './props';

export type Maybe<T> = T | undefined;

export enum Terminal {
  ZarinLink = 'zarinLink',
  PersonalLink = 'personalLink',
}

export enum EventType {
  ONLINE = 'ONLINE',
  LOCATION = 'LOCATION',
}
interface Merchant {
  name: string;
  avatar: string;
  title: string;
}

export interface PersonalMerchant extends Merchant {
  zp_id: string;
}

export interface ZarinMerchant extends Merchant {
  title: string;
  zp_id: string | null;
}

export type RequiredFields = {
  placeholder: string | null;
  status?: string;
};

export interface IFee {
  max_amount: number;
  percent: string;
  type: 'PAYER' | 'MERCHANT';
}

interface IRequest {
  is_new_system: boolean;
  is_telegram: boolean;
  referrer: string;
}

interface IRequestZarinLink extends IRequest {
  is_coupon_active: boolean;
}

export interface PersonalLink {
  merchant: PersonalMerchant;
  required_fields: Record<string, RequiredFields>;
  fee: IFee;
  request: IRequest;
  branding: IBranding;
}

export type Image = {
  id: string;
  url: string;
};

export type IPageType = 'PRODUCT' | 'PAYMENT' | 'RESERVATION' | 'SUBSCRIPTION';
export type IAmountType = 'FIX' | 'OPTIONAL';

export interface META_DATA_EVENT {
  start_time: string;
  end_time: string;
  event_type: EventType;
  start_date: string;
  end_date: string;
  url: string;
  province?: string;
  city?: string;
  address?: string;
}
export interface META_DATA_DONATION {
  target_amount: number;
  sum_amount_pay: number;
}
export type MetaDataType = META_DATA_DONATION | META_DATA_EVENT | null;
export interface IBrandTemplate {
  primary_color: string;
  secondary_color: string;
}
export interface IBranding {
  bio: string | null;
  contacts: {
    address: string | null;
    phone: string | null;
    email: string | null;
    socials: Record<string, string> | null;
  };
  template: IBrandTemplate;
}
export interface IError {
  code: number;
  description: string;
  message: string;
  status: LinkStatusType;
}
export interface ZarinLink extends PersonalLink {
  images: Image[];
  description: string;
  price?: number;
  amount: number;
  request: IRequestZarinLink;
  merchant: PersonalMerchant;
  out_of_stock: boolean;
  required_fields: Record<string, RequiredFields>;
  is_available: boolean;
  type: IPageType;
  amount_type: IAmountType;
  meta_data: MetaDataType;
  limit: number;
  specifications: Array<{ key: string; value: string }>;
  branding: IBranding;
  fee: IFee;
}

export interface IZarinLinkError extends ZarinLink {
  error?: IError;
}
export type AmountFieldDetail = {
  placeholder: string | null;
  rules: ValidationType[];
  value: string;
  name: string;
};

export type FieldDetail = {
  placeholder: string | null;
  rules: ValidationType[];
  value: string;
  name: string;
  amount?: AmountFieldDetail;
};

export type CouponInquiryParam = {
  code: string;
  product_id: string;
};
export interface FraudReportParam {
  linkType: IFraudReportLinkType;
  zarin_id: string;
  type: FraudReportEnumType;
  description: string | null;
  contact_cellphone: string | null;
}
export interface IFraudReportResponse {
  data: IFraudReport;
  meta: {
    code: number;
  };
}
export type DynamicForm = Record<string, FieldDetail>;

export type ReceiptParam = {
  authority: string;
};

export interface ProductType {
  title: string;
  description: string;
  type: 'PRODUCT';
  meta_data: META_DATA_EVENT | null;
  images: Image[];
}

export interface ReceiptType {
  amount: number;
  data: string;
  description: string;
  merchant: Merchant;
  payer_name: string;
  product: ProductType;
  status: 'OK' | 'NOK';
  ref_id?: string;
  session_id?: number;
  card_pan: string;
}
export interface ImerchantSession {
  name: string;
  avatar: string;
}
export interface IReceiptPayerDataItem {
  label: string;
  value: string;
}

export interface IReceiptPayerData {
  mobile?: IReceiptPayerDataItem;
  email?: IReceiptPayerDataItem;
  name?: IReceiptPayerDataItem;
  description?: IReceiptPayerDataItem;

  custom_field_1?: IReceiptPayerDataItem;

  custom_field_2?: IReceiptPayerDataItem;
}
export interface ISession {
  amount: number;
  ref_id: string | null;
  session_id: number;
  status: 'OK' | 'NOK';
  date: string;
  payer_data: IReceiptPayerData;
  card_pan: string | null;
  description: string;
  merchant: ImerchantSession;
}
export interface IReceiptCustomOptions {
  receipt_message_text: string;
  receipt_button_url: string;
}
export interface IReceiptData {
  branding: IBranding;
  session: ISession;
  zarin_link: ProductType;
  custom_options: IReceiptCustomOptions | null;
}
export enum FraudReportEnumType {
  ILLEGAL_GOODS_OR_SERVICES = 'ILLEGAL_GOODS_OR_SERVICES',
  FINANCIAL_FRAUD = 'FINANCIAL_FRAUD',
  SENSITIVE_INFORMATION_REQUEST = 'SENSITIVE_INFORMATION_REQUEST',
}
export interface IFraudReport {
  type: FraudReportEnumType;
  description: string;
  contact_cellphone: string;
}
export type IFraudReportLinkType = 'zarinLink' | 'personalLink';
export type LinkStatusType =
  | 'NOT_FOUND'
  | 'INACTIVE'
  | 'REACHED_DEADLINE'
  | 'REACHED_CAPACITY'
  | 'OUT_OF_STOCK'
  | 'REACHED_GOAL'
  | 'RECEIPT_INVALID'
  | 'RECEIPT_EXPIRED'
  | 'INVALID';
